import { ref, computed, readonly } from 'vue';
import type { Tenant, Team, UserSummary, PaymentType, ServiceId, UserRole } from '@/types/models';
import { INITIAL_TENANTS, INITIAL_TEAMS } from '@/services/mockData';
import {
  registerClerkAuthProviders,
  tenantsApi,
  teamsApi,
  billingApi,
  hasValidSessionOrToken,
  API_BASE_URL,
  CLERK_PUBLISHABLE_KEY,
} from '@/services/api';
import { getClerkInstance } from '@/services/clerkService';

// Available Persona Presets conforming to AI_RULES.md RBAC (ADMIN, COACH, PLAYER, GUARDIAN)
export const AVAILABLE_PERSONAS: UserSummary[] = [
  {
    id: 'usr_admin_kenji',
    fullName: '高橋 健二 (Kenji Takahashi)',
    email: 'k.takahashi@nextbase.co.jp',
    role: 'ADMIN',
    avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80',
  },
  {
    id: 'usr_coach_kudo',
    fullName: '工藤 裕樹 (Hiroki Kudo)',
    email: 'h.kudo@nextbase.co.jp',
    role: 'COACH',
    avatarUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&auto=format&fit=crop&q=80',
  },
  {
    id: 'usr_player_shota',
    fullName: '佐藤 翔太 (Shota Sato)',
    email: 's.sato18@example.jp',
    role: 'PLAYER',
    avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&auto=format&fit=crop&q=80',
  },
  {
    id: 'usr_parent_yumiko',
    fullName: '佐藤 由美子 (Yumiko Sato)',
    email: 'yumiko.sato@example.jp',
    role: 'GUARDIAN',
    avatarUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=120&auto=format&fit=crop&q=80',
  },
];

// Helper to initialize active user profile from persistent storage
function getInitialUser(): UserSummary {
  if (typeof window !== 'undefined') {
    const storedUserJson = localStorage.getItem('nbc_user_profile');
    if (storedUserJson) {
      try {
        const parsed = JSON.parse(storedUserJson);
        if (parsed && parsed.email && parsed.fullName) {
          // If it was the legacy demo user, migrate to the Japanese persona
          if (parsed.email === 'elena.admin@diamondacademy.com' || parsed.fullName === 'Elena Rostova') {
            return AVAILABLE_PERSONAS[0];
          }
          if (parsed.email === 'm.holloway@diamondacademy.com' || parsed.fullName?.includes('Holloway')) {
            return AVAILABLE_PERSONAS[1];
          }
          if (parsed.email === 'c.vance24@example.com' || parsed.fullName?.includes('Vance')) {
            return AVAILABLE_PERSONAS[2];
          }
          // Normalize legacy roles
          if (parsed.role === 'ORG_ADMIN' || parsed.role === 'TEAM_ADMIN') parsed.role = 'ADMIN';
          else if (parsed.role === 'HEAD_COACH' || parsed.role === 'ASSISTANT_COACH') parsed.role = 'COACH';
          else if (parsed.role === 'PARENT') parsed.role = 'GUARDIAN';
          return parsed;
        }
      } catch (e) {}
    }
  }
  return AVAILABLE_PERSONAS[0];
}

// Helper to load tenants from persistent storage or initial mock data
function getInitialTenants(): Tenant[] {
  if (typeof window !== 'undefined') {
    const storedCustom = localStorage.getItem('nbc_custom_tenants');
    if (storedCustom) {
      try {
        const parsed = JSON.parse(storedCustom);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // If cached data only has old placeholder English teams, reset to new Japanese tenants
          if (!parsed.some(t => t.name.includes('東京ガス') || t.name.includes('慶應') || t.name.includes('武蔵府中'))) {
            localStorage.removeItem('nbc_custom_tenants');
            localStorage.removeItem('nbc_custom_teams');
            localStorage.removeItem('nbc_active_tenant_data');
            return [...INITIAL_TENANTS];
          }
          return parsed;
        }
      } catch (e) {}
    }
    const singleTenant = localStorage.getItem('nbc_active_tenant_data');
    if (singleTenant) {
      try {
        const parsed = JSON.parse(singleTenant);
        if (parsed?.id && parsed?.name && (parsed.name.includes('東京ガス') || parsed.name.includes('慶應') || parsed.name.includes('武蔵府中'))) {
          return [parsed, ...INITIAL_TENANTS.filter(t => t.id !== parsed.id)];
        }
      } catch (e) {}
    }
  }
  return [...INITIAL_TENANTS];
}

function getInitialTenantId(): string {
  if (typeof window !== 'undefined') {
    const storedId = localStorage.getItem('nbc_active_tenant_id');
    if (storedId && INITIAL_TENANTS.some(t => t.id === storedId)) return storedId;
    const singleTenant = localStorage.getItem('nbc_active_tenant_data');
    if (singleTenant) {
      try {
        const parsed = JSON.parse(singleTenant);
        if (parsed?.id && INITIAL_TENANTS.some(t => t.id === parsed.id)) return parsed.id;
      } catch (e) {}
    }
  }
  return INITIAL_TENANTS[0].id;
}

function getInitialTeams(): Team[] {
  if (typeof window !== 'undefined') {
    const storedTeams = localStorage.getItem('nbc_custom_teams');
    if (storedTeams) {
      try {
        const parsed = JSON.parse(storedTeams);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed.some(t => t.name.includes('東京ガス') || t.name.includes('慶應') || t.name.includes('武蔵府中'))) {
          return parsed;
        }
      } catch (e) {}
    }
  }
  return [...INITIAL_TEAMS];
}

// Persistent / reactive tenant state
const availableTenants = ref<Tenant[]>(getInitialTenants());
const activeTenantId = ref<string>(getInitialTenantId());

const getInitialAuthState = (): boolean => {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  const hash = window.location.hash;
  if (
    pathname.startsWith('/auth') ||
    pathname === '/register-tenant' ||
    hash.includes('/auth') ||
    hash.includes('login') ||
    hash.includes('register-tenant')
  ) {
    return false;
  }
  const token = localStorage.getItem('nbc_auth_token') || localStorage.getItem('auth_token');
  const hasClerkSession = Boolean((window as any).Clerk?.session && (window as any).Clerk?.user);
  return Boolean(hasClerkSession || (token && token !== 'null' && !token.startsWith('bearer_session_')));
};

const isAuthenticated = ref<boolean>(getInitialAuthState());
const isLoading = ref<boolean>(false);

// Reactive teams list for updating payment models & services
export const managedTeams = ref<Team[]>(getInitialTeams());

// Active user profile (loaded from persistent localStorage / Clerk session)
const currentUser = ref<UserSummary>(getInitialUser());

// Active tenant computed (Self-Managed Tenant Scoping)
const activeTenant = computed<Tenant>(() => {
  const match = availableTenants.value.find((t) => t.id === activeTenantId.value);
  if (match) return match;

  if (typeof window !== 'undefined') {
    const storedData = localStorage.getItem('nbc_active_tenant_data');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed?.id && parsed?.name) return parsed;
      } catch (e) {}
    }
  }
  return availableTenants.value[0] || INITIAL_TENANTS[0];
});

// Teams belonging to current active tenant
const currentOrgTeams = computed<Team[]>(() => {
  return managedTeams.value.filter((t) => t.tenantId === activeTenantId.value);
});

// Tenant Subscription & Activated Services Checker
export const hasActiveSubscription = computed<boolean>(() => {
  const teams = currentOrgTeams.value;
  if (!teams || teams.length === 0) return false;
  return teams.some((team) =>
    Array.isArray(team.subscriptions) && team.subscriptions.some((sub) => sub.status === 'ACTIVE' || sub.status === 'cancel_wait')
  );
});

export const checkTenantSubscription = (tenantId?: string): boolean => {
  const targetId = tenantId || activeTenantId.value;
  const teams = managedTeams.value.filter((t) => t.tenantId === targetId);
  if (!teams || teams.length === 0) return false;
  return teams.some((team) =>
    Array.isArray(team.subscriptions) && team.subscriptions.some((sub) => sub.status === 'ACTIVE' || sub.status === 'cancel_wait')
  );
};

// Register Clerk Token Provider with Global API Client
registerClerkAuthProviders(
  async () => {
    if (typeof window !== 'undefined' && (window as any).Clerk?.session) {
      try {
        const token = await (window as any).Clerk.session.getToken();
        if (token) return token;
      } catch (e) {
        console.warn('[Clerk] Token fetch warning:', e);
      }
    }
    const stored = typeof window !== 'undefined' ? (localStorage.getItem('nbc_auth_token') || localStorage.getItem('auth_token')) : null;
    if (stored && stored !== 'null' && stored !== 'undefined' && !stored.startsWith('bearer_session_')) {
      return stored;
    }
    return null;
  },
  () => activeTenantId.value
);

// Hydration from API and Clerk User restoration (strictly self-managed tenant context)
async function loadFromApi() {
  if (typeof window === 'undefined') return;

  const pathname = window.location.pathname;
  const hash = window.location.hash;
  const isAuthPage =
    pathname.startsWith('/auth') ||
    pathname === '/register-tenant' ||
    hash.includes('/auth') ||
    hash.includes('login') ||
    hash.includes('register-tenant');

  if (isAuthPage || !hasValidSessionOrToken()) {
    return;
  }

  try {
    isLoading.value = true;

    // 1. Sync Clerk user profile (Clerk strictly for user auth)
    try {
      const clerk = await getClerkInstance();
      if (clerk?.user) {
        const email = clerk.user.primaryEmailAddress?.emailAddress || clerk.user.emailAddresses?.[0]?.emailAddress;
        const fullName = clerk.user.fullName || clerk.user.firstName || email?.split('@')[0] || 'User';
        const userProfile: UserSummary = {
          id: clerk.user.id,
          fullName,
          email: email || '',
          role: 'ADMIN',
          avatarUrl: clerk.user.imageUrl || currentUser.value.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80',
        };
        currentUser.value = userProfile;
        isAuthenticated.value = true;
        if (typeof window !== 'undefined') {
          localStorage.setItem('nbc_user_profile', JSON.stringify(userProfile));
          localStorage.setItem('nbc_user_id', clerk.user.id);
        }
      }
    } catch (clerkErr) {
      console.warn('[useTenant] Notice reading Clerk user:', clerkErr);
    }

    // 2. Fetch tenants and teams from backend API
    const [fetchedTenants, fetchedTeams] = await Promise.all([
      tenantsApi.getTenants(),
      teamsApi.getTeams(activeTenantId.value),
    ]);

    if (fetchedTenants && fetchedTenants.length > 0) {
      const mergedTenants = [...availableTenants.value];
      for (const t of fetchedTenants) {
        if (!mergedTenants.some(m => m.id === t.id)) {
          mergedTenants.push(t);
        }
      }
      availableTenants.value = mergedTenants;
    }

    if (fetchedTeams && fetchedTeams.length > 0) {
      const mergedTeams = [...managedTeams.value];
      for (const t of fetchedTeams) {
        const idx = mergedTeams.findIndex(m => m.id === t.id);
        if (idx !== -1) {
          mergedTeams[idx] = t;
        } else {
          mergedTeams.push(t);
        }
      }
      managedTeams.value = mergedTeams;
    }
  } catch (err) {
    console.info('[useTenant] Using local state cache:', err);
  } finally {
    isLoading.value = false;
  }
}

if (typeof window !== 'undefined') {
  const pathname = window.location.pathname;
  const hash = window.location.hash;
  const isAuthPage =
    pathname.startsWith('/auth') ||
    pathname === '/register-tenant' ||
    hash.includes('/auth') ||
    hash.includes('login') ||
    hash.includes('register-tenant');

  if (!isAuthPage && hasValidSessionOrToken()) {
    loadFromApi();
  }
}

export function useTenant() {
  const setActiveTenant = async (tenantId: string) => {
    const found = availableTenants.value.find((t) => t.id === tenantId);
    if (found) {
      activeTenantId.value = tenantId;
      if (typeof window !== 'undefined') {
        localStorage.setItem('nbc_active_tenant_id', tenantId);
        localStorage.setItem('nbc_active_tenant_data', JSON.stringify(found));
      }
      try {
        const teams = await teamsApi.getTeams(tenantId);
        if (teams && teams.length > 0) {
          const otherTeams = managedTeams.value.filter(t => t.tenantId !== tenantId);
          managedTeams.value = [...otherTeams, ...teams];
          if (typeof window !== 'undefined') {
            localStorage.setItem('nbc_custom_teams', JSON.stringify(managedTeams.value));
          }
        }
      } catch (e) {}
    }
  };

  const setPersona = (persona: UserSummary) => {
    currentUser.value = persona;
    isAuthenticated.value = true;
    if (typeof window !== 'undefined') {
      localStorage.setItem('nbc_user_profile', JSON.stringify(persona));
      localStorage.setItem('nbc_user_id', persona.id);
    }
  };

  const logout = async () => {
    isAuthenticated.value = false;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('nbc_user_profile');
      localStorage.removeItem('nbc_user_id');
      localStorage.removeItem('nbc_auth_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('nbc_active_persona_idx');
    }
    try {
      const clerk = await getClerkInstance();
      if (clerk?.signOut) {
        await clerk.signOut();
      }
    } catch (e) {}

    currentUser.value = AVAILABLE_PERSONAS[0];
    activeTenantId.value = INITIAL_TENANTS[0].id;
  };

  const createTenant = (newTenant: {
    name: string;
    slug?: string;
    tier?: import('@/types/models').TenantTier;
    type?: import('@/types/models').TenantTier;
    sportType?: string;
    seasonYear?: string;
    id?: string;
  }) => {
    const id = newTenant.id || `${Math.floor(10000000 + Math.random() * 90000000)}-${Date.now().toString(16)}-4000-8000-${Math.floor(100000000000 + Math.random() * 900000000000)}`;
    const fullTenant: Tenant = {
      name: newTenant.name,
      slug: newTenant.slug || newTenant.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      tier: newTenant.tier || newTenant.type || 'COLLEGIATE',
      type: newTenant.tier || newTenant.type || 'COLLEGIATE',
      sportType: newTenant.sportType || 'baseball',
      seasonYear: newTenant.seasonYear || '2025 Spring',
      id,
      stripeCustomerId: `cus_${id.slice(0, 8)}`,
      stripeId: `cus_${id.slice(0, 8)}`,
      stripeAccountId: `cus_${id.slice(0, 8)}`,
    };
    availableTenants.value.unshift(fullTenant);

    const defaultTeam: Team = {
      id: `team_${Date.now().toString(36)}`,
      tenantId: id,
      name: `${newTenant.name} Varsity`,
      season: newTenant.seasonYear || '2025 Spring',
      division: '18U Premier Showcase',
      headCoach: 'Team Manager',
      color: '#0284c7',
      wins: 0,
      losses: 0,
      ties: 0,
      playerCount: 0,
      paymentType: 'TEAM',
      isOwnTeam: true,
      homeField: 'Grand Slam Stadium',
      subscriptions: [
        {
          serviceId: 'BASEBALL_OPS_CORE',
          status: 'ACTIVE',
          paymentType: 'TEAM',
          currentPeriodEnd: '2025-12-31',
        },
      ],
    };
    managedTeams.value.unshift(defaultTeam);

    if (typeof window !== 'undefined') {
      localStorage.setItem('nbc_active_tenant_id', id);
      localStorage.setItem('nbc_active_tenant_data', JSON.stringify(fullTenant));
      localStorage.setItem('nbc_custom_tenants', JSON.stringify(availableTenants.value));
      localStorage.setItem('nbc_custom_teams', JSON.stringify(managedTeams.value));
    }

    activeTenantId.value = id;
    return fullTenant;
  };

  const updateTenantMetadata = (metadata: Partial<Tenant>) => {
    const targetId = activeTenantId.value;
    const idx = availableTenants.value.findIndex(t => t.id === targetId);
    if (idx !== -1) {
      availableTenants.value[idx] = { ...availableTenants.value[idx], ...metadata };
    }
    const updatedTenant = availableTenants.value[idx] || { ...activeTenant.value, ...metadata };
    if (typeof window !== 'undefined') {
      localStorage.setItem('nbc_active_tenant_data', JSON.stringify(updatedTenant));
      localStorage.setItem('nbc_custom_tenants', JSON.stringify(availableTenants.value));
    }
  };

  const updateTeamPaymentType = async (teamId: string, paymentType: PaymentType) => {
    const team = managedTeams.value.find(t => t.id === teamId);
    if (!team) return;
    team.paymentType = paymentType;
    if (typeof window !== 'undefined') {
      localStorage.setItem('nbc_custom_teams', JSON.stringify(managedTeams.value));
    }
    await teamsApi.updateTeam(teamId, { paymentType });
  };

  const setServicePaymentType = async (teamId: string, serviceId: ServiceId, paymentType: PaymentType) => {
    let team = managedTeams.value.find(t => t.id === teamId);
    if (!team && currentOrgTeams.value.length > 0) {
      team = currentOrgTeams.value[0];
    }
    if (!team) return;

    const existing = team.subscriptions.find(s => s.serviceId === serviceId);
    if (existing) {
      existing.paymentType = paymentType;
    } else {
      team.subscriptions.push({
        serviceId,
        status: 'INACTIVE',
        paymentType,
        currentPeriodEnd: '2025-12-31',
      });
    }

    if (typeof window !== 'undefined') {
      localStorage.setItem('nbc_custom_teams', JSON.stringify(managedTeams.value));
    }

    await billingApi.configureService(team.id, serviceId, paymentType);
  };

  const toggleTeamService = async (teamId: string, serviceId: ServiceId, enable: boolean, paymentType?: PaymentType) => {
    let team = managedTeams.value.find(t => t.id === teamId);
    if (!team && currentOrgTeams.value.length > 0) {
      team = currentOrgTeams.value[0];
    }
    if (!team) return;

    const effectivePaymentType: PaymentType = serviceId === 'VIDEO_ANALYTICS' ? 'TEAM' : (paymentType || team.paymentType);

    const existingIdx = team.subscriptions.findIndex(s => s.serviceId === serviceId);
    if (existingIdx !== -1) {
      team.subscriptions[existingIdx].status = enable ? 'ACTIVE' : 'INACTIVE';
      if (paymentType) {
        team.subscriptions[existingIdx].paymentType = effectivePaymentType;
      }
    } else if (enable) {
      team.subscriptions.push({
        serviceId,
        status: 'ACTIVE',
        paymentType: effectivePaymentType,
        currentPeriodEnd: '2025-12-31',
        stripeSubscriptionId: `sub_stripe_${Date.now()}`,
      });
    }

    if (typeof window !== 'undefined') {
      localStorage.setItem('nbc_custom_teams', JSON.stringify(managedTeams.value));
    }

    if (enable && effectivePaymentType === 'TEAM') {
      await billingApi.subscribeTeam(team.id, serviceId);
    } else {
      await billingApi.configureService(team.id, serviceId, effectivePaymentType);
    }
  };

  return {
    isAuthenticated: readonly(isAuthenticated),
    isLoading: readonly(isLoading),
    currentUser: readonly(currentUser),
    activeTenantId: readonly(activeTenantId),
    activeTenant,
    availableTenants: readonly(availableTenants),
    currentOrgTeams,
    managedTeams,
    hasActiveSubscription,
    checkTenantSubscription,
    apiBaseUrl: API_BASE_URL,
    clerkPublishableKey: CLERK_PUBLISHABLE_KEY,
    refreshData: loadFromApi,
    setActiveTenant,
    setPersona,
    logout,
    createTenant,
    updateTenantMetadata,
    updateTeamPaymentType,
    setServicePaymentType,
    toggleTeamService,
  };
}
